import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac Escalade IQL (linkId 2375, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2375.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * IQL (long wheelbase) is structurally identical to 2374's IQ: same
 * backgrounds, same 750 HP/1064 NM/724 km performance figures, same
 * 4-variant Luxury/Premium Luxury/Sport/Premium Sport lineup with 0
 * shared configurationIds. Pricing: all-zero (showPrice:false in 1.0),
 * same treatment as 2374/2379/2383.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    EscaladeIQL: {
      id: 'EscaladeIQL',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        luxury: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5650',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5657',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5670',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5525',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5663',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'premium-luxury': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5650',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5657',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5673',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5525',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5653',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        sport: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5650',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5657',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5670',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5525',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5653',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        'premium-sport': {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5650',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5657',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5673',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5525',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5662',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '5525',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '5457',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#ff1300,#0040FF,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientRed: {
            isDefault: true,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.5,
          },
          ambientBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.5,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.5,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.5,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.5,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              'premium-luxury': 'interior-color',
              sport: 'interior-color',
              'premium-sport': 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.5,
          },
        },
        relevantVariants: ['luxury', 'premium-luxury', 'sport', 'premium-sport'],
      },
      colors: {
        exterior: {
          '30ja6fs1si26': {
            sku: '5651',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          '1d0y9evbmngc': {
            sku: '5657',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          mwsv1fnhg5wz: {
            sku: '5652',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          onwrm9wz90ia: {
            sku: '5654',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          bpsnuu0maoxi: {
            sku: '5658',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          v0g8zhggpwls: {
            sku: '5666',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          s48xyts50cye: {
            sku: '5651',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          '5jdcjsfwdj7d': {
            sku: '5657',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          '1xk1ykeonvpa': {
            sku: '5652',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          l0xax3vaun9r: {
            sku: '5654',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          lur9l2wn8yu8: {
            sku: '5658',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          ha3g9fc7n9eh: {
            sku: '5666',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
          },
          '6c5y3678ccei': {
            sku: '5651',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          savklced7txj: {
            sku: '5657',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          '204txwa69u4j': {
            sku: '5652',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          psfdqk9f2qez: {
            sku: '5654',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          hwlr62qfm18q: {
            sku: '5658',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          rhc1r76oe9i9: {
            sku: '5666',
            project: '59',
            relevantVariants: [
              'sport',
            ],
          },
          '4f9dd3vca75z': {
            sku: '5651',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
          '69xux2hlscm1': {
            sku: '5657',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
          '0pazineb3kjj': {
            sku: '5652',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
          qzewltf6e2pf: {
            sku: '5654',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
          '10xy9i682nxc': {
            sku: '5658',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
          '37wp0lpjh309': {
            sku: '5666',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
          },
        },
        interior: {
          '03wk11dwf99s': {
            sku: '5670',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
            // Close the door before the new interior material swaps in - it
            // wasn't authored against an open-door pose.
            affectedInteractionOnApply: {
              ihmz2ch7lru2: { add: false, snapToLastFrame: true },
            },
          },
          j50tistnihpo: {
            sku: '5665',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              ihmz2ch7lru2: { add: false, snapToLastFrame: true },
            },
          },
          nk7jbkvai3yb: {
            sku: '5673',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              pdvesjvy4q3c: { add: false, snapToLastFrame: true },
            },
          },
          uw02zrjb3rky: {
            sku: '5656',
            project: '59',
            relevantVariants: [
              'premium-luxury',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              pdvesjvy4q3c: { add: false, snapToLastFrame: true },
            },
          },
          x8lk86kmni6a: {
            sku: '5670',
            project: '59',
            relevantVariants: [
              'sport',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              nsrbl8k7p41m: { add: false, snapToLastFrame: true },
            },
          },
          ybkz5spqcgr8: {
            sku: '5665',
            project: '59',
            relevantVariants: [
              'sport',
            ],
            notCompatibleExteriorColors: [],
            affectedInteractionOnApply: {
              nsrbl8k7p41m: { add: false, snapToLastFrame: true },
            },
          },
          '1c0z0wtq7ikc': {
            sku: '5673',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
            notCompatibleExteriorColors: [],
            // The Executive Pack (2nd row seat) accessory's applyMessages
            // pick between Camelia/Harbor Blue via a `(sku) is (interior-color)`
            // condition, but that condition is only evaluated when the
            // accessory's own messages are (re)dispatched - a later interior
            // color change doesn't retrigger it on its own, leaving the seat
            // stuck on whichever color was active at variant load. Reload
            // whichever of the group is currently active so it re-evaluates.
            accessoriesToReload: ['wtnujr9hlztg', 'az7g6geux1c2'],
            affectedInteractionOnApply: {
              f6mckp6y4528: { add: false, snapToLastFrame: true },
            },
          },
          '0rl5mcxrsw1t': {
            sku: '5656',
            project: '59',
            relevantVariants: [
              'premium-sport',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['wtnujr9hlztg', 'az7g6geux1c2'],
            affectedInteractionOnApply: {
              f6mckp6y4528: { add: false, snapToLastFrame: true },
            },
          },
        },
      },
      features: {
        '50fyijsa8h88': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Exterior Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zarbo501dlfo: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1e0q63itjg52': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        x5p4553jsccd: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Roof_Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ljxlvsxjaks3: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Quater_window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quater window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sdkeibj4zedx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        wfyhlnfxcntc: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Sound System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9v7g6wt62yu6': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Transmisson Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Transmisson_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        g6h9z7ho9usu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ja3evostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'AC Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        domb9mqy9v1p: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'Exterior Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9o691919xh7h': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9wuk2z7wdj19': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7mavle88rcl3': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Roof_Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        pzig1mfltekl: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Quater_window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quater window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        d5vu1c93bze6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        dx9xtc3riked: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'Sound System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        s18ecud9kcc1: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'Transmisson Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Transmisson_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bslxv6wdsy1n: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '5rt2ii0juuv9': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'AC Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fu508kf9w3nt: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Seat_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Seat Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        snovea3006kg: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-luxury',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sb5wl8rp4nx7: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Exterior Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        u37xry51xqzj: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        xdods4ovhx4e: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bhhmhkhq3d3h: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Roof_Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sky3x313nzhx: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Quater_window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quater window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '183dnoen9l4x': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'SHADER_Info_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        gdg0qvyqiedi: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Sound System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        jbj5m4h8sf5v: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Transmisson Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Transmisson_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sd7jyd0thaar: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '39d3hdoktii9': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'sport',
          ],
          messages: [
            {
              message: 'AC Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '0gvs8s0ln4fh': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'Exterior Design',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        nb8tkmndj3vg: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m37v8o8yl5x2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m37v8of8yl5x2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Roof_Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        m37v8o8yl5xc2: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Quater_window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Quater window',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        xogfbqwjubb0: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Info_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        s4f98dj7g3mk: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'Sound System',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        epg4m463go0z: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'Transmisson Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Transmisson_screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        tvh5xpuhzsbz: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ngzrs5lwkgop: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'AC Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ngzers5lwkgop: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'SHADER_Rear_Seat_Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Rear Seat Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ngzfers5lwkgop: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'premium-sport',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        bk4m2jlqj8aj: {
          view: 'exterior',
          groupId: 'luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Seat_Display_Sheer_Gray',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SEAT_SCREEN',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_SEAT_DISPLAY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        f0lhueo2zmkj: {
          view: 'interior',
          groupId: 'luxury-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Seat_Display_Sheer_Gray',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SEAT_SCREEN',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_SEAT_DISPLAY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jesolvzcok7i: {
          view: 'exterior',
          groupId: 'luxury-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jvdyh6y5ykjq: {
          view: 'exterior',
          groupId: 'luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'EXT_Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2enukijm6irm': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Luxury',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '69m81c2fb2br': {
          view: 'interior',
          groupId: 'premium-luxury-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Executive_Pack_Camelia_Backen',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5673) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'WO_Executive_Pack_Harbor_Blue',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5656) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        uk1lwiwxwb3u: {
          view: 'interior',
          groupId: 'premium-luxury-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Executive_Pack_Camelia_Backen',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5673) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_Executive_Pack_Harbor_Blue',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5656) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        fflbumwjqutt: {
          view: 'exterior',
          groupId: 'premium-luxury-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        afxoyorodb61: {
          view: 'exterior',
          groupId: 'premium-luxury-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'EXT_Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '0tktmmeohiq1': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Premium_Luxury',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        az7g6geux1c1: {
          view: 'exterior',
          groupId: 'sport-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Seat_Display_Sheer_Gray',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SEAT_SCREEN',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_SEAT_DISPLAY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9b2d90cotnka': {
          view: 'interior',
          groupId: 'sport-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Seat_Display_Sheer_Gray',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_SEAT_SCREEN',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_SEAT_DISPLAY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jdk7n05xg2ys: {
          view: 'exterior',
          groupId: 'sport-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2odyke55ebug': {
          view: 'exterior',
          groupId: 'sport-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'EXT_Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '4ywaz56unxw2': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Sport',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        wtnujr9hlztg: {
          view: 'interior',
          groupId: 'premium-sport-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Executive_Pack_Camelia_Backen',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5673) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'WO_Executive_Pack_Harbor_Blue',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5656) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        az7g6geux1c2: {
          view: 'interior',
          groupId: 'premium-sport-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Executive_Pack_Camelia_Backen',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5673) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'W_Executive_Pack_Harbor_Blue',
              identifiers: [
                'common_exterior',
              ],
              condition: '(5656) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        quf319yxtxl8: {
          view: 'exterior',
          groupId: 'premium-sport-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'EXT_Roof',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        j2oqomprlsyv: {
          view: 'exterior',
          groupId: 'premium-sport-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'EXT_Black_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        zj5ykaueh3zt: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Premium_Sport',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'premium-sport',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {},
      interactions: {
        ihmz2ch7lru2: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        '53ehrrkf0yxy': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        pq3yykiuv4iu: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUSPENSION_UP',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUSPENSION_DOWN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        w3yjdzjao2i0: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        toijdriu1av5: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        g64qit5yzfp2: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        pdvesjvy4q3c: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        tipo90ysous9: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        mnle5v0tmw01: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUSPENSION_UP',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUSPENSION_DOWN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        plvdfamzavhn: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        e6cj50layhmo: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        kheb0pgg4q7d: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-luxury',
          ],
        },
        nsrbl8k7p41m: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        q5i2b8dp8qiv: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        lsbgt8ai1q3w: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUSPENSION_UP',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUSPENSION_DOWN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        xusdipi0pcny: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        hctuiocvp393: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        '9835qgl47bif': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'sport',
          ],
        },
        f6mckp6y4528: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
        tbur04pwvnag: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
        lpgfndx8gmly: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUSPENSION_UP',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUSPENSION_DOWN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
        bkr8amy2tvbs: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
        '6146uzbd6kui': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
        iq7p37hq4g0j: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'premium-sport',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_3',
            identifiers: [
              'common_exterior',
            ],
          },
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
